import { Auth } from 'aws-amplify';
import React from 'react';

import { ConversationSection } from '~/types/api.graphql';

import { ConversationEntityType, stripLeadingNewlines } from './AIInsightsModal.utils';

export enum Trigger {
  CtaOpen = 'cta_open',
  UserMessage = 'user_message',
  Action = 'action',
}

enum StreamChunkType {
  Text = 'text',
  Thinking = 'thinking',
  Action = 'action',
}

const SSE_DATA_PREFIX = 'data: ';
const SSE_DONE_SIGNAL = '[DONE]';
const PAYLOAD_VERSION = '1';

export interface SuggestedAction {
  key: string;
  title: string;
  description: string;
}

interface StreamResult {
  text: string;
  actions: SuggestedAction[];
}

export interface EntryPoint {
  section: ConversationSection;
  entityId: string;
  entityType: ConversationEntityType;
  ctaSource: string;
}

interface UseBedrockStreamParams {
  sessionIdRef: React.MutableRefObject<string | null>;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  streamingBufferRef: React.MutableRefObject<string>;
  entryPoint: EntryPoint | null;
  onToolStatus?: (status: string | null) => void;
}

const refreshToken = async (): Promise<void> => {
  const user = await Auth.currentAuthenticatedUser();
  const currentSession = user.signInUserSession;
  return new Promise((resolve, reject) => {
    user.refreshSession(currentSession.refreshToken, (error) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
  });
};

const getBedrockEndpoint = (): string => {
  const localEndpoint = process.env.REACT_APP_AI_INSIGHTS_LOCAL_ENDPOINT;

  if (localEndpoint) {
    return localEndpoint;
  }

  const bedrockEndpointUrl = process.env.REACT_APP_BEDROCK_AGENT_CORE_ENDPOINT_URL;
  const agentArn = process.env.REACT_APP_BEDROCK_AGENT_ARN;

  if (!bedrockEndpointUrl || !agentArn) {
    throw new Error('Bedrock endpoint or agent ARN not configured');
  }

  const escapedAgentArn = encodeURIComponent(agentArn);
  return `${bedrockEndpointUrl}/runtimes/${escapedAgentArn}/invocations?qualifier=DEFAULT`;
};

const sendMessageWithRetry = async (
  payload: Record<string, unknown>,
  sessionIdRef: React.MutableRefObject<string | null>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
  retryCount = 0,
): Promise<Response> => {
  // Get authentication tokens
  const session = await Auth.currentSession();
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();

  // Session ID should be set by the modal (section-identifier format)
  if (!sessionIdRef.current) {
    throw new Error('Session ID is not set. Modal should initialize it before sending messages.');
  }

  const origin = window.location.origin;
  const bedrockEndpoint = getBedrockEndpoint();
  const response = await fetch(bedrockEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Id-Token': idToken,
      'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionIdRef.current,
      Origin: origin,
    },
    body: JSON.stringify(payload),
    signal: abortControllerRef.current?.signal || undefined,
  });

  // If we get a 401 and haven't retried yet, refresh token and retry
  if (response.status === 401 && retryCount === 0) {
    try {
      await refreshToken();
      return sendMessageWithRetry(payload, sessionIdRef, abortControllerRef, 1);
    } catch (refreshError) {
      console.error('Error refreshing token:', refreshError);
      throw new Error('Failed to refresh authentication token');
    }
  }

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.status} ${response.statusText}`);
  }

  return response;
};

const processStreamChunks = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  streamingBufferRef: React.MutableRefObject<string>,
  onToolStatus?: (status: string | null) => void,
): Promise<StreamResult> => {
  const decoder = new TextDecoder();
  let assistantMessage = '';
  let buffer = '';
  let hasReceivedText = false;
  const actions: SuggestedAction[] = [];

  const processChunk = async (): Promise<void> => {
    const { done, value } = await reader.read();

    if (done) {
      return;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.trim() === '') continue;

      // Handle Server-Sent Events format - Bedrock sends "data: <json-object>"
      if (line.startsWith(SSE_DATA_PREFIX)) {
        const data = line.slice(SSE_DATA_PREFIX.length).trim();
        if (data === SSE_DONE_SIGNAL) {
          return;
        }
        try {
          const parsed = JSON.parse(data);
          if (parsed && typeof parsed === 'object' && parsed.type === StreamChunkType.Text) {
            if (!hasReceivedText) {
              hasReceivedText = true;
              onToolStatus?.(null);
            }
            assistantMessage += parsed.content;
          } else if (parsed && typeof parsed === 'object' && parsed.type === StreamChunkType.Thinking) {
            onToolStatus?.(parsed.content as string);
          } else if (parsed && typeof parsed === 'object' && parsed.type === StreamChunkType.Action) {
            actions.push({
              key: parsed.action.key,
              title: parsed.action.title,
              description: parsed.action.description,
            });
          } else if (typeof parsed === 'string') {
            // Backward compatibility: plain quoted strings
            assistantMessage += parsed;
          }
        } catch {
          // If parsing fails, use the data as-is
          assistantMessage += data;
        }
      } else if (line.trim() && !line.startsWith('event:') && !line.startsWith('id:')) {
        assistantMessage += line;
      }

      streamingBufferRef.current = stripLeadingNewlines(assistantMessage);
    }

    // Continue reading next chunk
    await processChunk();
  };

  await processChunk();
  return { text: stripLeadingNewlines(assistantMessage), actions };
};

export type TriggerPayload = { prompt: string } | { action: string } | Trigger.CtaOpen;

const buildPayload = (triggerPayload: TriggerPayload, entryPoint: EntryPoint | null): Record<string, unknown> => {
  const isCtaOpen = triggerPayload === Trigger.CtaOpen;
  const trigger = isCtaOpen ? Trigger.CtaOpen : 'prompt' in triggerPayload ? Trigger.UserMessage : Trigger.Action;
  return {
    version: PAYLOAD_VERSION,
    trigger,
    ...(isCtaOpen ? {} : triggerPayload),
    timezone: String(-new Date().getTimezoneOffset()),
    timestamp: new Date().toISOString(),
    ...(entryPoint ? { entryPoint } : {}),
  };
};

export const useBedrockStream = ({
  sessionIdRef,
  abortControllerRef,
  streamingBufferRef,
  entryPoint,
  onToolStatus,
}: UseBedrockStreamParams) => {
  const sendMessage = async (payload: TriggerPayload): Promise<StreamResult> => {
    const fullPayload = buildPayload(payload, entryPoint);
    const response = await sendMessageWithRetry(fullPayload, sessionIdRef, abortControllerRef);

    const reader = response.body?.getReader();

    if (!reader) {
      throw new Error('No reader available');
    }

    return processStreamChunks(reader, streamingBufferRef, onToolStatus);
  };

  return { sendMessage };
};
